import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useCreateKnowledgeBaseCollectionMutation } from '@/core/services/knowledge-base';
import { getApiErrorMessage } from '@/utils/errors';
import { CreateKnowledgeBaseCollectionDto } from '@/core/models/knowledge-base-collection.model';

type CreateCollectionModalProps = {
    open: boolean;
    onClose: () => void;
};

const validationSchema = z.object({
    name: z.string().min(1, 'Collection name is required'),
    description: z.string().optional(),
});

function CreateCollectionModal({ open, onClose }: CreateCollectionModalProps) {
    const { enqueueSnackbar } = useSnackbar();
    const [createCollection, { isLoading }] = useCreateKnowledgeBaseCollectionMutation();
    const { control, handleSubmit, formState: { errors } } = useForm<CreateKnowledgeBaseCollectionDto>({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            name: '',
            description: ''
        }
    });

    const onSubmit: SubmitHandler<CreateKnowledgeBaseCollectionDto> = async (data) => {
        try {
            await createCollection(data).unwrap();
            enqueueSnackbar('Collection created successfully', { variant: 'success' });
            onClose();
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'Failed to create collection'), { variant: 'error' });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create New Collection</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Collection Name"
                                fullWidth
                                margin="dense"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Description (Optional)"
                                fullWidth
                                margin="dense"
                                multiline
                                rows={4}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={isLoading}>
                        {isLoading ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default CreateCollectionModal; 